import PubSub from 'pubsub-js';
import { Mesh, Object3D } from 'three';

import { SkyViewContextMenuProps } from '../../react/components/skyview/context-menu';
import { VolumeCalculationMethod } from '../../react/components/skyview-new/components/volume-calculation-tool/volume-calculation-helper';
import { MetaDefinition } from '../../services/metadata/meta-types';
import { publishNotification } from '../../utils/notifications/publish-notification';
import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { Geometry } from '../elements/geometry';
import { VolumeBoundaryData, VolumeCalculationAsset } from '../elements/volume/types';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import {
  MouseButtonType,
  MouseInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { Tool } from './tool';
import { VolumeBoundaryPolygon } from './utils/volume-boundary-polygon';

export enum VolumeCalculatorToolState {
  Selecting,
  DefineCadBoundingPolygon,
  SelectCadBoundingAsset,
  SelectAreaBoundingAsset,
  Disabled,
}
export class VolumeCalculatorTool extends Tool {
  get hasAssetSelected() {
    return this.selectedAsset !== undefined;
  }

  get getState() {
    return this.state;
  }

  get hasValidBoundary() {
    return this.volumeBoundaryPolygon.visible && this.volumeBoundaryPolygon.hasValidPolygon;
  }

  private state: VolumeCalculatorToolState;
  // private meta: MetaDefinition;

  private contextMenuCallback?: (menu?: SkyViewContextMenuProps) => void;
  private selectedAsset?: VolumeCalculationAsset;
  private volumeBoundaryPolygon: VolumeBoundaryPolygon;
  private meta: MetaDefinition;

  enable() {
    this.visible = true;
    this.inputHandler.setCursor('crosshair');
    this.state = VolumeCalculatorToolState.Selecting;
  }

  constructor(inputHandler: InputHandler) {
    super('volume-calculator', inputHandler);
    this.visible = false;
    this.volumeBoundaryPolygon = new VolumeBoundaryPolygon();
    this.volumeBoundaryPolygon.visible = false;
    this.meta = { src: 'tool.volume-calculator', parameters: [] };

    this.state = VolumeCalculatorToolState.Disabled;

    PubSub.subscribe(
      'start_volume_calculation',
      async (
        e: string,
        data: {
          volumeName: string;
          targetData: {
            meshes?: Mesh[];
            asset?: VolumeCalculationAsset;
          };
          sourceData?: {
            meshes?: Mesh[];
            asset?: VolumeCalculationAsset;
            pointCloudId?: string;
            sourceTerrainModelGeoDataId?: string;
          };
          boundaryData?: VolumeBoundaryData;
          method?: VolumeCalculationMethod;
        },
      ) => {
        await this.startVolumeCalculation(
          data.volumeName,
          data.targetData,
          data.sourceData,
          data.boundaryData,
          data.method,
        );
      },
    );
  }

  getMeta(sweref?: string): MetaDefinition {
    return this.meta;
  }

  getVolumeBoundaryPolygon() {
    return this.volumeBoundaryPolygon;
  }

  onMouseOut(mouseInputData: MouseInputData): boolean {
    if (this.volumeBoundaryPolygon && this.volumeBoundaryPolygon.isEditing) {
      this.volumeBoundaryPolygon.displayEditingGeometry(false);
    }

    return true;
  }

  setSelectedAsset(asset: VolumeCalculationAsset | undefined) {
    this.selectedAsset = asset;
  }

  getSelectedAsset() {
    return this.selectedAsset;
  }

  onAnimate() {
    this.volumeBoundaryPolygon.animate();
  }

  onMouseIn(mouseInputData: MouseInputData) {
    if (this.volumeBoundaryPolygon && this.volumeBoundaryPolygon.isEditing) {
      this.volumeBoundaryPolygon.displayEditingGeometry(true);
    }
    return true;
  }

  onMouseMove(e: MouseInputData): boolean {
    if (this.state === VolumeCalculatorToolState.DefineCadBoundingPolygon) {
      this.volumeBoundaryPolygon.onMouseMove(e);
    }

    return true;
  }

  showContextMenu(_input: MouseInputData) {
    // Context menu is handled in onMouseUp via the React contextMenuCallback.
    // Returning false here prevents the legacy PubSub path in handleContextMenu
    // from intercepting the right-click and short-circuiting onMouseUp.
    return false;
  }

  getContextMenuItems(input: MouseInputData) {
    return this.volumeBoundaryPolygon.getContextMenuItems(input);
  }

  onContextMenuChanged(callback: (menu?: SkyViewContextMenuProps) => void) {
    this.contextMenuCallback = callback;
  }

  onMouseDown(e: MouseInputData) {
    this.mousePosition = e.pointer.client;

    return true;
  }

  // TODO: Legacy code, needs to be refacored
  async onMouseUp(e: MouseInputData) {
    if (!this.withinThreshold(e.pointer.client.x, e.pointer.client.y)) {
      return Promise.resolve(true);
    }

    if (e.button.type === MouseButtonType.RightButton) {
      if (this.volumeBoundaryPolygon.showContextMenu(e)) {
        const items = this.volumeBoundaryPolygon.getContextMenuItems(e);
        this.contextMenuCallback?.({
          pos: { x: e.viewportPos?.clientX ?? 0, y: e.viewportPos?.clientY ?? 0 },
          items: items.map((item) => ({
            title: item.title,
            action: () => {
              item.click();
              this.contextMenuCallback?.();
            },
          })),
        });
        return Promise.resolve(true);
      }

      // If area creation is ingoing, abort current creation.
      // Else hide modal and switch tool
      if (
        this.state === VolumeCalculatorToolState.DefineCadBoundingPolygon &&
        this.volumeBoundaryPolygon.vertexCount > 0
      ) {
        if (this.volumeBoundaryPolygon.isEdgeLocked) {
          this.volumeBoundaryPolygon.unlockEdge();
        } else if (this.volumeBoundaryPolygon.isVertexPicked) {
          this.volumeBoundaryPolygon.releasePickedVertex();
        } else {
          this.volumeBoundaryPolygon.endTriangleCreation();
          this.state =
            this.volumeBoundaryPolygon.triangleCount > 0
              ? VolumeCalculatorToolState.Selecting
              : VolumeCalculatorToolState.DefineCadBoundingPolygon;
        }
      } else {
        this.volumeBoundaryPolygon.clearCustom();
        this.inputHandler.setTool('select');

        this.state = VolumeCalculatorToolState.Selecting;
      }

      return Promise.resolve(true);
    }

    if (!this.mousePosition || this.state === VolumeCalculatorToolState.Disabled) {
      return Promise.resolve(true);
    }

    if (e.button.type === MouseButtonType.LeftButton) {
      switch (this.state) {
        case VolumeCalculatorToolState.Selecting:
          if (this.volumeBoundaryPolygon.isIntersecting(e)) {
            this.state = VolumeCalculatorToolState.DefineCadBoundingPolygon;

            return Promise.resolve(true);
          } else if (this.volumeBoundaryPolygon.visible === false) {
            const asset = AssetManager.instance.pick(
              e.pointer.position.x,
              e.pointer.position.y,
              cameraManager().getActiveCamera()!.getTHREECamera(),
            );
            AssetManager.instance.select(asset);
          }
          break;
        case VolumeCalculatorToolState.DefineCadBoundingPolygon:
          if (!this.selectedAsset) {
            return Promise.resolve(true);
          }
          this.volumeBoundaryPolygon.onMouseUp(e);
          return Promise.resolve(true);
        case VolumeCalculatorToolState.SelectCadBoundingAsset:
          break;
      }
    }

    return Promise.resolve(true);
  }

  findParentAsset(intersection: Object3D): Geometry | undefined {
    if (!intersection.parent) {
      return;
    }

    if ((intersection.parent as Geometry).objType === undefined) {
      return this.findParentAsset(intersection.parent);
    }

    return intersection.parent as Geometry;
  }

  /**
   *  Toggle the state for the cad sub area polygon.
   * @param state - State if the cad sub polygon area should be enabled or not.
   */
  setVolumeCalculatorToolState(state: VolumeCalculatorToolState) {
    this.state = state;
    switch (state) {
      case VolumeCalculatorToolState.Selecting:
      case VolumeCalculatorToolState.SelectAreaBoundingAsset:
      case VolumeCalculatorToolState.SelectCadBoundingAsset:
      case VolumeCalculatorToolState.Disabled:
        this.volumeBoundaryPolygon.visible = false;
        this.volumeBoundaryPolygon.endTriangleCreation();

        break;
      case VolumeCalculatorToolState.DefineCadBoundingPolygon:
        this.volumeBoundaryPolygon.visible = true;
        this.volumeBoundaryPolygon.startTriangleCreation();
        break;
      default:
        break;
    }
  }

  closeVolumeCalculationUI = () => {
    this.inputHandler.setTool('select');
  };

  cleanUp = () => {
    this.volumeBoundaryPolygon.clearCustom();
    this.state = VolumeCalculatorToolState.Selecting;
  };

  resetTargetForVolumeBoundaryPolygon(asset: VolumeCalculationAsset | undefined) {
    if (asset) {
      this.volumeBoundaryPolygon.reset(asset);
    }
  }

  private async startVolumeCalculation(
    volumeName: string,
    targetData: {
      meshes?: Mesh[];
      asset?: VolumeCalculationAsset;
    },
    sourceData?: {
      meshes?: Mesh[];
      asset?: VolumeCalculationAsset;
      pointCloudId?: string;
      sourceTerrainModelGeoDataId?: string;
    },
    boundaryData?: VolumeBoundaryData,
    method?: VolumeCalculationMethod,
  ) {
    if (!targetData?.asset || !targetData?.meshes || !sourceData) {
      return;
    }

    this.closeVolumeCalculationUI();

    switch (this.state) {
      case VolumeCalculatorToolState.Selecting:
      case VolumeCalculatorToolState.DefineCadBoundingPolygon: {
        const userLimitationMesh = this.hasValidBoundary;
        this.volumeBoundaryPolygon.endTriangleCreation();
        await targetData.asset.volumeHandler.calculate(
          volumeName,
          targetData.meshes,
          sourceData,
          userLimitationMesh && this.volumeBoundaryPolygon.limitationMesh
            ? { mesh: this.volumeBoundaryPolygon.limitationMesh }
            : undefined,
          method,
        );
        break;
      }
      case VolumeCalculatorToolState.SelectCadBoundingAsset:
      case VolumeCalculatorToolState.SelectAreaBoundingAsset:
        if (boundaryData) {
          await targetData.asset.volumeHandler.calculate(
            volumeName,
            targetData.meshes,
            sourceData,
            boundaryData,
            method,
          );
        } else {
          publishNotification('err1109');
        }
        break;
    }
    publish(SubscriptionTopic.AssetChanged, targetData.asset);
    this.cleanUp();
  }
}
